import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.service_contract_ */
export type ServiceContractId = string & { __brand: 'ServiceContractId' }

/** Represents the table public.service_contract_ */
export default interface ServiceContractTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ServiceContractId,
    ServiceContractId | undefined,
    ServiceContractId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId | null, ProjectId | null, ProjectId | null>

  /** Database type: pg_catalog.int4 */
  contractNumber: ColumnType<number, number | undefined, number>

  /** Database type: pg_catalog.int4 */
  totalPriceInCents: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  notes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  invoiceDate: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.text */
  invoiceNumber: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<OrganizationId, OrganizationId, OrganizationId>
}

export type ServiceContract = Selectable<ServiceContractTable>

export type ServiceContractInitializer = Insertable<ServiceContractTable>

export type ServiceContractMutator = Updateable<ServiceContractTable>

export const serviceContractId =
  z.string() as unknown as z.Schema<ServiceContractId>

export const serviceContract = z.object({
  id: serviceContractId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  projectId: projectId.nullable().optional(),
  contractNumber: z.number(),
  totalPriceInCents: z.number(),
  notes: z.string().nullable().optional(),
  invoiceDate: z.coerce.date().nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  organizationId: organizationId,
}) as unknown as z.Schema<ServiceContract>

export const serviceContractInitializer = z.object({
  id: serviceContractId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  projectId: projectId.optional().nullable(),
  contractNumber: z.number().optional(),
  totalPriceInCents: z.number(),
  notes: z.string().optional().nullable(),
  invoiceDate: z.coerce.date().optional().nullable(),
  invoiceNumber: z.string().optional().nullable(),
  organizationId: organizationId,
}) as unknown as z.Schema<ServiceContractInitializer>

export const serviceContractMutator = z.object({
  id: serviceContractId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  projectId: projectId.optional().nullable(),
  contractNumber: z.number().optional(),
  totalPriceInCents: z.number().optional(),
  notes: z.string().optional().nullable(),
  invoiceDate: z.coerce.date().optional().nullable(),
  invoiceNumber: z.string().optional().nullable(),
  organizationId: organizationId.optional(),
}) as unknown as z.Schema<ServiceContractMutator>
